import { yupResolver } from "@hookform/resolvers/yup"
import { Box, Button, Typography } from "@jewlr/storybook/core"
import { FloatInput, Select, Textarea } from "@jewlr/storybook/form"
import React, { useState } from "react"
import { Helmet } from "react-helmet"
import { useForm, Controller } from "react-hook-form"
import styled from "styled-components"
import * as Yup from "yup"

import api from "apis"
import { Paragraph } from "areas/order/components"

const ContactMessage = styled(Textarea)`
  height: 85px;
  margin-bottom: 0;
  ${({ theme }) => theme.mediaQueries.tablet`
    flex-basis: 100%;
  `};
`
const Container = styled.div`
  margin: 0 auto 40px;
  padding: 20px 20px 0;

  ${(props) => props.theme.mediaQueries.tablet`
    max-width: 725px;
    padding: 20px 0 0;
  `};
`

const getCurrentTimeInTimeZone = (timezone) => {
  let options = {
    hour: "numeric",
    hour12: false,
    minute: "numeric",

    timeZone: timezone,
  }

  return new Date().toLocaleTimeString([], options).replace(":", "")
}
const current_time_string = getCurrentTimeInTimeZone("America/Toronto")

const formValidationSchema = Yup.object().shape({
  case_type: Yup.string().required("Please make a selection"),
  customer_email: Yup.string()
    .email("Invalid email address")
    .required("Your email is required"),
  customer_name: Yup.string()
    .matches(/\w+(\s+\w+)+/, { message: "Please enter your full name" })
    .required("Your name is required"),
  customer_phone: Yup.string()
    .matches(/^[\d\-+(). ]{10,20}$/, {
      message: "Please enter a correct phone number",
    })
    .required("Your phone number is required"),
  message: Yup.string().required("Please provide your message"),
  order_number: Yup.string().matches(/^(J|j|L|l|B|b)[\d]{10}(-\d{1,7})?$/, {
    excludeEmptyString: true,
    message: "Please enter a proper order number",
  }),
})

export const Contact = () => {
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      case_type: "",
      customer_email: "",
      customer_name: "",
      customer_phone: "",
      message: "",
      order_number: "",
    },
    mode: "onBlur",
    resolver: yupResolver(formValidationSchema),
  })
  const [status, setStatus] = useState(false)

  const onSubmit = async (values) => {
    try {
      let url = "/secure/api/v10/order/contact"
      let response = await api.post(url, values)
      if (response && response.data.done) {
        setStatus(response.data.done)
      }
    } catch (err) {
      console.log(err)
      setError("root.error", {
        message: err?.errors?.[0] || err?.message || err,
      })
    }
  }

  return (
    <>
      <Helmet>
        <title>Contact Us</title>
      </Helmet>
      <Container>
        {status ? (
          <Paragraph>
            Thank you for contacting us. Please allow up to two business days
            for our representatives to get back to you.
          </Paragraph>
        ) : (
          <>
            <form onSubmit={handleSubmit(onSubmit)}>
              {errors?.root?.error && (
                <Typography color="error" mb={1}>
                  {errors.root.error.message}
                </Typography>
              )}
              <Controller
                control={control}
                name="customer_name"
                render={({ field, fieldState: { error } }) => (
                  <Box mb={2}>
                    <FloatInput error={error} label="Your Name:" {...field} />
                  </Box>
                )}
              />
              <Controller
                control={control}
                name="customer_email"
                render={({ field, fieldState: { error } }) => (
                  <Box mb={2}>
                    <FloatInput error={error} label="Your Email:" {...field} />
                  </Box>
                )}
              />
              <Controller
                control={control}
                name="customer_phone"
                render={({ field, fieldState: { error } }) => (
                  <Box mb={2}>
                    <FloatInput error={error} label="Your Phone:" {...field} />
                  </Box>
                )}
              />
              <Controller
                control={control}
                name="order_number"
                render={({ field, fieldState: { error } }) => (
                  <Box mb={2}>
                    <FloatInput
                      error={error}
                      label="Order # (if you have one):"
                      {...field}
                    />
                  </Box>
                )}
              />
              <Controller
                control={control}
                name="case_type"
                render={({ field, fieldState: { error } }) => (
                  <Box mb={2}>
                    <Select color="black" error={error} width="100%" {...field}>
                      <option disabled selected value="">
                        How can we help?
                      </option>
                      <option value="Inquiry">
                        Need information before ordering
                      </option>
                      <option value="Payment Issue">Payment Issues</option>
                      <option value="Shipping Upgrade">
                        Upgrade my shipping method
                      </option>
                      <option value="Order Status">
                        Where&apos;s my order?
                      </option>
                      <option value="Change Request">
                        Make a change to my order
                      </option>
                      <option value="Unsubscribe">
                        Remove me from your mailing list
                      </option>
                      <option value="Other">Other</option>
                    </Select>
                  </Box>
                )}
              />
              <Controller
                control={control}
                name="message"
                render={({ field, fieldState: { error } }) => (
                  <Box mb={2}>
                    <ContactMessage
                      error={error}
                      placeholder="Your Message:"
                      type="textbox"
                      {...field}
                    />
                  </Box>
                )}
              />
              <Box mb={2} width="100%">
                <Button
                  loading={isSubmitting}
                  primary
                  type="submit"
                  width="100%"
                >
                  {isSubmitting ? "Sending..." : "Send"}
                </Button>
              </Box>
            </form>
            <Paragraph>
              For further assistance, feel free to directly email us at{" "}
              <a href={`mailto:${sl.config.customer_service_email}`}>
                {sl.config.customer_service_email}
              </a>{" "}
              {current_time_string >= "0900" &&
              current_time_string <= "1730" ? (
                <>
                  or call us at{" "}
                  <a href={`tel:${sl.config.customer_service_toll_free}`}>
                    {sl.config.customer_service_toll_free}
                  </a>
                </>
              ) : (
                ""
              )}{" "}
              .
            </Paragraph>
          </>
        )}
      </Container>
    </>
  )
}

export default Contact
